// Admin Sidebar Component — dark blue theme with grouped nav sections
import React, { useEffect, useState, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { BrandConfig as activeBrand } from '../../app/config/brand';
import type { IconsType, IconComponent } from '../../types/admin';

type PlatformRole = 'operator' | 'admin';

interface SidebarNavItem {
  id: string;
  label: string;
  icon: string;
  badgeKey?: string;
}

interface SidebarSection {
  id: string;
  label: string;
  visibleTo: PlatformRole[];
  items: SidebarNavItem[];
  mergeWithPrevious?: string;
}

// Section-based navigation configuration
const sidebarSections: SidebarSection[] = [
  {
    id: 'work',
    label: 'WORK',
    visibleTo: ['operator', 'admin'],
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'Dashboard' },
      { id: 'queue', label: 'Queue', icon: 'Document', badgeKey: 'queue' },
      { id: 'my-work', label: 'My Work', icon: 'Folder', badgeKey: 'myWork' },
      { id: 'tasks', label: 'Tasks', icon: 'Check', badgeKey: 'tasks' },
      { id: 'orders', label: 'Orders', icon: 'Briefcase' },
    ],
  },
  {
    id: 'people',
    label: 'PEOPLE',
    visibleTo: ['operator', 'admin'],
    items: [
      { id: 'users', label: 'Users', icon: 'User' },
      { id: 'leaderboard', label: 'Leaderboard', icon: 'Chart' },
    ],
  },
  {
    id: 'people-admin',
    label: 'PEOPLE',
    visibleTo: ['admin'],
    mergeWithPrevious: 'people',
    items: [
      { id: 'team', label: 'Team', icon: 'ProcessServing' },
      { id: 'invitations', label: 'Invite', icon: 'User' },
    ],
  },
  {
    id: 'settings',
    label: 'SETTINGS',
    visibleTo: ['admin'],
    items: [
      { id: 'setup', label: 'Setup', icon: 'Settings' },
      { id: 'finance', label: 'Finance', icon: 'Payment' },
    ],
  },
  {
    id: 'insights',
    label: 'INSIGHTS',
    visibleTo: ['admin'],
    items: [
      { id: 'reports', label: 'Reports', icon: 'Chart' },
      { id: 'performance', label: 'Performance', icon: 'Chart' },
    ],
  },
];

// Build sections based on platform role
const getSections = (platformRole: PlatformRole): SidebarSection[] => {
  const visibleSections = sidebarSections.filter(
    section => section.visibleTo.includes(platformRole)
  );

  const mergedSections: SidebarSection[] = [];
  const processed = new Set<string>();

  for (const section of visibleSections) {
    if (processed.has(section.id)) continue;

    if (section.mergeWithPrevious) {
      const baseIndex = mergedSections.findIndex(s => s.id === section.mergeWithPrevious);
      if (baseIndex !== -1) {
        mergedSections[baseIndex] = {
          ...mergedSections[baseIndex],
          items: [...mergedSections[baseIndex].items, ...section.items],
        };
      }
    } else {
      mergedSections.push({ ...section });
    }
    processed.add(section.id);
  }

  return mergedSections;
};

// Hook for persisting section collapsed state
const useSidebarSectionState = () => {
  const STORAGE_KEY = 'admin-sidebar-sections';

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Ignore localStorage errors
      }
      return next;
    });
  }, []);

  const isSectionCollapsed = useCallback((sectionId: string): boolean => {
    return !!collapsedSections[sectionId];
  }, [collapsedSections]);

  return { toggleSection, isSectionCollapsed };
};

interface HamburgerIconProps {
  onClick: () => void;
}

const HamburgerIcon: React.FC<HamburgerIconProps> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
    style={{ backgroundColor: SIDEBAR_BG }}
    aria-label="Toggle menu"
  >
    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  </button>
);

interface CloseIconProps {
  onClick: () => void;
}

const CloseIcon: React.FC<CloseIconProps> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden absolute top-4 right-4 z-50 p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
    style={{ backgroundColor: 'transparent' }}
    aria-label="Close menu"
  >
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: SIDEBAR_TEXT }}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>
);

// Fallback icons for when Icons prop doesn't include them
const FallbackChevronDown = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const FallbackPlusIcon = ({ className }: { className?: string }) => (
  <svg className={className || 'w-5 h-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

// Design tokens — the active brand's sidebar set (fixed for the page's lifetime).
const SIDEBAR_BG = activeBrand.colors.sidebarBg;
const SIDEBAR_TEXT = activeBrand.colors.sidebarText;
const SIDEBAR_HOVER = activeBrand.colors.sidebarHover;
const SIDEBAR_ACTIVE_BG = 'rgba(255, 255, 255, 0.1)';
const SIDEBAR_ACTIVE_INDICATOR = activeBrand.colors.sidebarActive;
const ACCENT = activeBrand.colors.primary;
const ACCENT_HOVER = activeBrand.colors.primaryHover;
const ON_ACCENT = activeBrand.colors.onPrimary;
const TOOLTIP_BG = activeBrand.colors.secondary;
const BADGE_BG = ACCENT;

interface NavItemBadgeProps {
  count?: number;
  isCollapsed: boolean;
}

const NavItemBadge: React.FC<NavItemBadgeProps> = ({ count, isCollapsed }) => {
  if (!count || count <= 0) return null;

  const displayCount = count > 99 ? '99+' : count;

  if (isCollapsed) {
    return (
      <span
        className="absolute top-1 right-1 w-2 h-2 rounded-full"
        style={{ backgroundColor: BADGE_BG }}
      />
    );
  }

  return (
    <span
      className="ml-auto px-2 py-0.5 text-xs font-medium rounded-full"
      style={{
        backgroundColor: BADGE_BG,
        color: ON_ACCENT,
      }}
    >
      {displayCount}
    </span>
  );
};

interface AdminSidebarNavItemProps {
  item: SidebarNavItem;
  isActive: boolean;
  isCollapsed: boolean;
  onClick: (id: string) => void;
  Icons: IconsType;
  badgeCount?: number;
}

const AdminSidebarNavItem: React.FC<AdminSidebarNavItemProps> = ({ item, isActive, isCollapsed, onClick, Icons, badgeCount }) => {
  const IconComp: IconComponent = Icons[item.icon] || Icons.Document;

  return (
    <button
      onClick={() => onClick(item.id)}
      onMouseEnter={(e) => {
        if (!isActive) {
          (e.currentTarget as HTMLElement).style.backgroundColor = SIDEBAR_HOVER;
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
        }
      }}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors relative group ${isCollapsed ? 'justify-center' : ''}`}
      style={{
        backgroundColor: isActive ? SIDEBAR_ACTIVE_BG : 'transparent',
        color: isActive ? 'white' : SIDEBAR_TEXT,
      }}
      title={isCollapsed ? item.label : undefined}
      data-testid={`nav-item-${item.id}`}
    >
      {isActive && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r"
          style={{ backgroundColor: SIDEBAR_ACTIVE_INDICATOR }}
        />
      )}
      <IconComp className="w-5 h-5 shrink-0" />
      {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}

      <NavItemBadge count={badgeCount} isCollapsed={isCollapsed} />

      {/* Tooltip on hover when collapsed */}
      {isCollapsed && (
        <div
          className="absolute left-full ml-2 px-2 py-1 rounded text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50"
          style={{ backgroundColor: TOOLTIP_BG, color: 'white' }}
        >
          {item.label}
          {badgeCount && badgeCount > 0 && ` (${badgeCount > 99 ? '99+' : badgeCount})`}
        </div>
      )}
    </button>
  );
};

interface SectionHeaderProps {
  section: SidebarSection;
  isCollapsed: boolean;
  isSectionCollapsed: boolean;
  onToggle: (sectionId: string) => void;
  Icons: IconsType;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ section, isCollapsed, isSectionCollapsed, onToggle, Icons }) => {
  if (isCollapsed) return null;

  const ChevronDown = Icons.ChevronDown || FallbackChevronDown;

  return (
    <button
      onClick={() => onToggle(section.id)}
      className="w-full flex items-center justify-between px-3 py-2 mt-2 text-[11px] font-semibold tracking-widest uppercase transition-opacity"
      style={{ color: SIDEBAR_TEXT, opacity: 0.5 }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.8'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.5'; }}
      data-testid={`section-header-${section.id}`}
      aria-expanded={!isSectionCollapsed}
    >
      <span>{section.label}</span>
      <span
        className="transition-transform duration-200"
        style={{ transform: isSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
      >
        <ChevronDown />
      </span>
    </button>
  );
};

interface SidebarSectionProps {
  section: SidebarSection;
  currentModule: string;
  isCollapsed: boolean;
  isSectionCollapsed: boolean;
  onToggleSection: (sectionId: string) => void;
  onClick: (id: string) => void;
  Icons: IconsType;
  badgeCounts: Record<string, number>;
}

const SidebarSectionComponent: React.FC<SidebarSectionProps> = ({
  section,
  currentModule,
  isCollapsed,
  isSectionCollapsed,
  onToggleSection,
  onClick,
  Icons,
  badgeCounts,
}) => {
  const showItems = isCollapsed || !isSectionCollapsed;

  return (
    <div className="mb-1" data-testid={`sidebar-section-${section.id}`}>
      <SectionHeader
        section={section}
        isCollapsed={isCollapsed}
        isSectionCollapsed={isSectionCollapsed}
        onToggle={onToggleSection}
        Icons={Icons}
      />
      {showItems && (
        <div className="space-y-0.5">
          {section.items.map(item => (
            <AdminSidebarNavItem
              key={item.id}
              item={item}
              isActive={currentModule === item.id}
              isCollapsed={isCollapsed}
              onClick={onClick}
              Icons={Icons}
              badgeCount={item.badgeKey ? badgeCounts?.[item.badgeKey] : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface NewButtonProps {
  isCollapsed: boolean;
  onClick: () => void;
  Icons: IconsType;
  /** Fill the row (the header stacks the button under a full logo). */
  fullWidth?: boolean;
}

const NewButton: React.FC<NewButtonProps> = ({ isCollapsed, onClick, Icons, fullWidth = false }) => {
  const PlusIcon = Icons.Plus || FallbackPlusIcon;

  if (isCollapsed) {
    return (
      <button
        onClick={onClick}
        className="w-10 h-10 rounded-lg flex items-center justify-center transition-all group relative"
        style={{ backgroundColor: ACCENT, color: ON_ACCENT }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ACCENT_HOVER; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ACCENT; }}
        title="New Order"
        data-testid="new-order-button"
      >
        <PlusIcon className="w-5 h-5" />
        <div
          className="absolute left-full ml-2 px-2 py-1 rounded text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50"
          style={{ backgroundColor: TOOLTIP_BG, color: 'white' }}
        >
          New Order
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${fullWidth ? 'w-full justify-center' : ''}`}
      style={{ backgroundColor: ACCENT, color: ON_ACCENT }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ACCENT_HOVER; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ACCENT; }}
      data-testid="new-order-button"
    >
      <PlusIcon className="w-4 h-4" />
      <span>New Order</span>
    </button>
  );
};

interface User {
  name?: string;
  email?: string;
}

interface AdminSidebarProps {
  currentModule: string;
  onModuleChange: (moduleId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen: boolean;
  onMobileToggle: () => void;
  brand: BrandConfig;
  Icons: IconsType;
  user?: User;
  onLogout: () => void;
  platformRole?: PlatformRole;
  badgeCounts?: Record<string, number>;
  onNewOrder?: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  currentModule,
  onModuleChange,
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onMobileToggle,
  brand,
  Icons,
  user,
  onLogout,
  platformRole = 'admin',
  badgeCounts = {},
  onNewOrder,
}) => {
  const sections = getSections(platformRole);
  const { toggleSection, isSectionCollapsed } = useSidebarSectionState();

  const ChevronLeft = Icons.ChevronLeft;
  const ChevronRight = Icons.ChevronRight;

  const handleModuleClick = (moduleId: string) => {
    onModuleChange(moduleId);
    if (isMobileOpen && onMobileToggle) {
      onMobileToggle();
    }
  };

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileOpen && onMobileToggle) {
        onMobileToggle();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileOpen, onMobileToggle]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const userInitial = user?.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <>
      {/* Mobile hamburger button */}
      {!isMobileOpen && (
        <HamburgerIcon onClick={onMobileToggle} />
      )}

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onMobileToggle}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed left-0 top-0 h-full flex flex-col transition-all duration-300 z-40
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        style={{
          width: isCollapsed ? '80px' : '256px',
          backgroundColor: SIDEBAR_BG,
        }}
        data-testid="admin-sidebar"
      >
        {/* Mobile close button */}
        {isMobileOpen && (
          <CloseIcon onClick={onMobileToggle} />
        )}

        {/* Logo + New Button Header */}
        <div className="p-4" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <div className={`flex ${isCollapsed ? 'flex-col items-center gap-3' : brand.logos.dark ? 'flex-col gap-3' : 'items-center justify-between'}`}>
            <button
              onClick={() => handleModuleClick('dashboard')}
              className={`flex items-center gap-3 transition-opacity hover:opacity-90 ${!isCollapsed && brand.logos.dark ? 'w-full justify-between' : ''}`}
              data-testid="admin-sidebar-logo"
            >
              {!isCollapsed && brand.logos.dark ? (
                // A brand with dark-ground art shows its full logo, as the customer sidebar does.
                <>
                  <img src={brand.logos.dark} alt={brand.company.name} className="block h-8 w-auto" />
                  <span className="text-[11px]" style={{ color: SIDEBAR_TEXT, opacity: 0.6 }}>
                    Admin Portal
                  </span>
                </>
              ) : brand.logos.iconDark ?? brand.logos.icon ? (
                <img
                  src={(brand.logos.iconDark ?? brand.logos.icon) as string}
                  alt=""
                  className="block w-9 h-9 shrink-0"
                />
              ) : (
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs shrink-0"
                  style={{ backgroundColor: ACCENT, color: ON_ACCENT }}
                >
                  {brand.company.shortName}
                </div>
              )}
              {!isCollapsed && !brand.logos.dark && (
                <div className="flex flex-col items-start">
                  <span className="text-white font-semibold text-sm leading-tight">
                    {brand.company.name}
                  </span>
                  <span className="text-[11px]" style={{ color: SIDEBAR_TEXT, opacity: 0.6 }}>
                    Admin Portal
                  </span>
                </div>
              )}
            </button>
            {onNewOrder && (
              <NewButton
                isCollapsed={isCollapsed}
                onClick={onNewOrder}
                Icons={Icons}
                fullWidth={Boolean(brand.logos.dark)}
              />
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto">
          {sections.map(section => (
            <SidebarSectionComponent
              key={section.id}
              section={section}
              currentModule={currentModule}
              isCollapsed={isCollapsed}
              isSectionCollapsed={isSectionCollapsed(section.id)}
              onToggleSection={toggleSection}
              onClick={handleModuleClick}
              Icons={Icons}
              badgeCounts={badgeCounts}
            />
          ))}
        </nav>

        {/* Bottom: User Chip + Collapse Toggle */}
        <div className="px-3 py-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
          {/* User chip */}
          {user && (
            <div className={`mb-2 ${isCollapsed ? 'flex justify-center' : ''}`}>
              {isCollapsed ? (
                <button
                  onClick={onLogout}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium group relative"
                  style={{ backgroundColor: ACCENT, color: ON_ACCENT }}
                  title={`${user.name || 'User'} — Sign Out`}
                >
                  {userInitial}
                  <div
                    className="absolute left-full ml-2 px-2 py-1 rounded text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50"
                    style={{ backgroundColor: TOOLTIP_BG, color: 'white' }}
                  >
                    {user.name || 'User'} — Sign Out
                  </div>
                </button>
              ) : (
                <div
                  className="flex items-center gap-2.5 px-2 py-2 rounded-lg transition-colors"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0"
                    style={{ backgroundColor: ACCENT, color: ON_ACCENT }}
                  >
                    {userInitial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate">{user.name || 'User'}</div>
                    <button
                      onClick={onLogout}
                      className="text-xs hover:underline min-h-[44px] sm:min-h-0 leading-tight"
                      style={{ color: SIDEBAR_TEXT, opacity: 0.7 }}
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Collapse Toggle — hidden on mobile */}
          <button
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = SIDEBAR_HOVER; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
            className="hidden lg:flex w-full items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors"
            style={{ color: SIDEBAR_TEXT, opacity: 0.6 }}
            data-testid="collapse-toggle"
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-xs font-medium">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
